// 모바일/원격 클라이언트 디버깅
// ① ?debug=1 → eruda(인페이지 F12 콘솔) 로드 — 폰 화면에서 콘솔·네트워크·에러 직접 확인.
// ② 에러 자동 리포트 — console.error / window.onerror / unhandledrejection 을 링버퍼에 모아
//    디바운스 배치로 POST /api/feedback/client-log → 서버가 back/logs/client_debug/YYYY-MM-DD.jsonl
//    에 저장. 운영자가 폰을 안 보고 있어도 에러가 서버 파일로 남아 Claude 가 SSH 로 읽고 해결.
// 정식 운영 전환 시 Sentry 류로 교체 고려 — 그 전까지 외부 의존 0 자체 구현.

const RING_MAX = 80;        // 최근 로그 보관 개수
const SEND_DEBOUNCE = 8000; // 에러 후 8초 모아서 전송(연쇄 에러 배치)
// 세션 첫 에러만 빨리 보낸다 — 모바일은 OS 가 VRAM/메모리 압박에 탭을
// **통째로 죽인다**. 그러면 JS 가 한 줄도 못 돌아 pagehide 플러시조차 안 오고, 8초 창에
// 걸린 로그는 통째로 유실된다("모바일에서 갑자기 튕겼는데 서버엔 아무것도 없음").
// 첫 신호만 1.2초에 흘려 「터지기 직전까지의 정황」을 확보하고, 이후 연쇄는 8초로 묶는다.
const FIRST_DEBOUNCE = 1200;
const MAX_SENDS = 6;        // 세션당 전송 상한(폭주 방지)

const ring = [];
const history = [];
const sessionId = Math.random().toString(36).slice(2, 10);  // 이 탭 한 번의 접속을 잇는 열쇠
let sendTimer = null;
let sendCount = 0;
let installed = false;

export function getRecentClientLogs(limit = 30) {
  return history.slice(-Math.max(0, Math.min(80, limit))).map((entry) => ({ ...entry }));
}

// ── 크래시를 «게임 안에서» 읽히게 ────────────────────────────────────────────
// 화면에는 한 줄짜리 한국어만 낸다. 스택트레이스는 콘솔·서버 로그에 그대로 두고 안 띄운다.
const ERR_KO = [
  [/Cannot read propert(y|ies) of (null|undefined)/i, '게임 내부 오류 — 아직 준비되지 않은 값을 읽으려 했습니다'],
  [/is not a function/i,                              '게임 내부 오류 — 없는 기능을 부르려 했습니다'],
  [/Failed to fetch|NetworkError|Load failed/i,       '서버에 요청을 보내지 못했습니다 — 네트워크를 확인해 주세요'],
  [/ChunkLoadError|Loading chunk|dynamically imported module/i, '게임 파일을 받지 못했습니다 — 새로고침이 필요합니다'],
  [/WebGL|context lost|CONTEXT_LOST/i,                '그래픽 장치 연결이 끊겼습니다 — 새로고침해 주세요'],
  [/out of memory|Allocation failed/i,                '메모리가 부족합니다 — 다른 탭을 닫고 새로고침해 주세요'],
];
const errKo = (raw) => {
  const t = String(raw || '');
  for (const [re, ko] of ERR_KO) if (re.test(t)) return ko;
  return '게임에 오류가 발생했습니다';
};

// 같은 문구는 20초에 한 번만 — 크래시는 한 프레임에 수십 번 연발한다.
const _shownAt = new Map();
function noticeOnce(raw) {
  try {
    const ko = errKo(raw);
    const now = Date.now();
    if (now - (_shownAt.get(ko) || 0) < 20000) return;
    _shownAt.set(ko, now);
    // 정적 import 금지 — 이 모듈은 앱보다 먼저 뜬다. 표시부가 없으면 그냥 안 뜬다.
    // 크래시 알림은 소리 없이 — 거절음은 «사용자가 누른 버튼의 실패»에만 낸다.
    window.__toast?.err?.(ko, { detail: String(raw || '').slice(0, 120), silent: true });
  } catch { /* 알리다 실패하는 건 원래 에러보다 중요하지 않다 */ }
}

function push(level, args) {
  try {
    const msg = args.map((a) => {
      if (a instanceof Error) return `${a.name}: ${a.message}\n${(a.stack || '').slice(0, 600)}`;
      if (typeof a === 'object') { try { return JSON.stringify(a).slice(0, 400); } catch { return String(a); } }
      return String(a);
    }).join(' ').slice(0, 1200);
    // 같은 에러 연발은 한 칸에 접는다. 매 프레임 터지는 에러(물 셰이더
    // undefined.value 2,732건 / 셰이더 링크 실패 17연발)가 80칸 링버퍼를 통째로 채워
    // **정작 원인인 첫 로그가 밀려나** 있었다. 직전 항목과 같으면 개수만 올린다.
    const last = ring[ring.length - 1];
    if (last && last.level === level && last.msg === msg) {
      last.n = (last.n || 1) + 1;
      last.tLast = Date.now();
      return;
    }
    // ★ 라우트를 **사건이 난 순간에** 박는다. payload 의 `url` 은 flush 시점
    //   값이라(아래 flush 참조), 게임에서 죽고 로그인으로 튕겨 나온 뒤 배치가 나가면
    //   `url=https://.../login` 으로 찍힌다 — 8/23~8/26 원장 17건이 그래서 「로그인 화면에서
    //   죽는다」로 읽혔고, 로그인 화면은 3D 캔버스를 띄우지도 않는다는 사실과 모순돼 한참을
    //   헤맸다. 사건의 자리와 보고의 자리는 다른 값이다. 둘 다 남긴다.
    const entry = { t: Date.now(), level, msg };
    try { entry.rt = window.location.pathname; } catch { /* noop */ }
    ring.push(entry);
    history.push({ ...entry });
    if (history.length > RING_MAX) history.shift();
    // 오래된 것부터 버리되 **첫 20칸은 지킨다** — 사고의 시작이 거기 있다. 연발이 길어지면
    // 종전엔 처음 로그가 전부 밀려나고 마지막 증상만 남았다(원인 대신 결과만 보임).
    if (ring.length > RING_MAX) ring.splice(20, 1);
    // GLTFLoader 텍스처 blob 실패 — blob URL 은 매번 새 임의값이라 메시지만으론 원본 GLB 를
    // 못 찾는다. 최근 요청한 GLB 목록을
    // 비동기로 붙여 다음 발생 때는 로그만 보고 범인을 알 수 있게 한다.
    if (level === 'error' && msg.includes('GLTFLoader') && msg.includes('blob')) {
      import('./glbCacheProbe.js').then((m) => {
        const recent = m.getRecentGltfLoads?.().slice(-6).join(',');
        if (recent) entry.msg += ` | 최근GLB: ${recent}`;
      }).catch(() => {});
    }
  } catch { /* noop — 로거가 앱을 죽이면 안 됨 */ }
}

function flush() {
  if (sendCount >= MAX_SENDS || ring.length === 0) return;
  if (sendTimer) { clearTimeout(sendTimer); sendTimer = null; }
  sendCount++;
  const payload = {
    ua: navigator.userAgent,
    url: window.location.href,
    screen: `${window.screen?.width}x${window.screen?.height} dpr${window.devicePixelRatio}`,
    // sess — 같은 접속의 여러 배치를 한 줄로 묶는 열쇠. 종전엔 ua+화면크기로
    // 세션을 **추측**했는데, 같은 폰으로 두 번 들어오면 한 사람으로 뭉쳐 「몇 번 터졌나」를
    // 셀 수 없었다. seq 는 그 안의 순번 — 중간 배치가 유실됐는지가 번호 구멍으로 드러난다.
    sess: sessionId,
    seq: sendCount,
    // mem/cpu — 모바일 크래시의 대부분은 메모리다. 기종(ua)만으론 같은 모델의 4GB/8GB 를
    // 못 가른다. deviceMemory 는 iOS 미지원이라 없을 수 있다(그 자체로 iOS 신호).
    mem: navigator.deviceMemory || null,
    cpu: navigator.hardwareConcurrency || null,
    // prof/gfx — **어떤 양으로 그리다 죽었나**. 이게 없으면 크래시 로그를 보고도
    // 「프로파일이 안 걸려서 터진 건지, 걸렸는데도 터진 건지」를 못 가른다 — 그 둘은
    // 다음 수가 정반대다(배선 수리 vs 더 낮추기). 자동 판정과 실제 적용값을 둘 다 싣는다.
    prof: window.__deviceProfile?.profile || null,
    gfx: window.__gfxApplied ? { ...window.__gfxApplied, view: window.__viewDistanceM || null } : null,
    entries: ring.splice(0, ring.length),
  };
  // sendBeacon 우선(페이지 죽어도 전송), 실패 시 fetch. 응답은 안 기다림.
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (!navigator.sendBeacon?.('/api/feedback/client-log', blob)) {
      fetch('/api/feedback/client-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
    }
  } catch { /* noop */ }
}

function scheduleSend() {
  if (sendCount >= MAX_SENDS || sendTimer) return;
  sendTimer = setTimeout(() => { sendTimer = null; flush(); },
    sendCount === 0 ? FIRST_DEBOUNCE : SEND_DEBOUNCE);
}

// 렌더 진단(stalled/data-error/shader-error/context-lost) 즉시 리포트 —
// "지형 안 그려짐"은 console.error 를 안 내므로 진단 버스가 직접 이걸 호출해야 잡힌다.
export function reportEventNow(level, msg) {
  push(level, [msg]);
  flush();
}

export function initClientLogReporter() {
  if (installed) return;
  installed = true;

  // console.error/warn 후킹 (원본 유지 — 재귀 방지: 후킹 안에서 console 호출 금지)
  const origErr = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...a) => { push('error', a); scheduleSend(); origErr(...a); };
  console.warn = (...a) => { push('warn', a); origWarn(...a); };

  window.addEventListener('error', (e) => {
    push('onerror', [e.message, `${e.filename}:${e.lineno}:${e.colno}`, e.error]);
    noticeOnce(e.message);
    scheduleSend();
  });
  window.addEventListener('unhandledrejection', (e) => {
    push('unhandledrejection', [e.reason]);
    noticeOnce(e.reason?.message || e.reason);
    scheduleSend();
  });
  // 페이지 이탈 시 잔여 로그 플러시 — "안 그려지네" 하고 8초 안에 닫아도 유실 안 됨.
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  // ?debug=1 → eruda 인페이지 콘솔(F12급). CDN 동적 로드 — 일반 유저 비용 0.
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('debug') === '1') {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/eruda';
      s.onload = () => { try { window.eruda?.init(); } catch { /* noop */ } };
      document.head.appendChild(s);
    }
  } catch { /* noop */ }
}
